import { api } from './client';

/**
 * B1 · El DIRECTORIO del copiloto. Prefijo: /api/copiloto/usuarios. SOLO ADMIN.
 *
 * Se lee y se guarda APARTE de la configuración, con su propio botón por fila,
 * porque no es lo mismo: la config es un borrador con un "Guardar" al final;
 * esto es una lista de personas con acceso, y ahí un cambio a medias —o un
 * "Guardar" que alguien nunca apretó— es un problema de verdad.
 */

export interface UsuarioCopiloto {
  id: string;
  /** `null`: solo le habla desde el panel. */
  telefono: string | null;
  /** Usuario del panel atado. `null`: solo le habla por WhatsApp. */
  userId: string | null;
  nombre: string;
  rol: 'dueno' | 'equipo';
  alcance: string;
  activo: boolean;
  /** El usuario atado, para mostrarlo. */
  usuario?: { name: string | null; email: string } | null;
  /** Lo que escribió al asistente en los últimos 30 días. */
  mensajes30d?: number;
  ultimoMensaje?: string | null;
}

/**
 * Lo que viaja al guardar. Sin `id` = alta.
 *
 * Usuario, teléfono, o los dos: al menos uno. Sin nombre y con usuario, el
 * server usa el nombre del usuario.
 */
export interface UsuarioCopilotoInput {
  id?: string;
  telefono?: string | null;
  userId?: string | null;
  nombre?: string;
  rol: 'dueno' | 'equipo';
  alcance?: string;
  activo?: boolean;
}

/** Todo el directorio, incluidos los desactivados. */
export const listarUsuarios = () => api.get<UsuarioCopiloto[]>('/api/copiloto/usuarios');

export const guardarUsuario = (input: UsuarioCopilotoInput) =>
  api.put<UsuarioCopiloto>('/api/copiloto/usuarios', input);

export const eliminarUsuario = (id: string) =>
  api.del<{ ok: boolean }>(`/api/copiloto/usuarios/${id}`);

/**
 * EL ACCESO AL ASISTENTE, por usuario del panel. SOLO ADMIN.
 *
 * Es la misma entrada del directorio, vista desde Usuarios. Quitar el acceso
 * la pausa: lo que la persona habló queda en el historial.
 */
export interface AccesoCopiloto {
  userId: string;
  acceso: null | {
    entryId: string;
    rol: 'dueno' | 'equipo';
    activo: boolean;
    telefono: string | null;
  };
  mensajes30d: number;
  ultimoMensaje: string | null;
}

export const listarAccesos = () => api.get<AccesoCopiloto[]>('/api/copiloto/accesos');

export const darAcceso = (userId: string, rol: 'dueno' | 'equipo') =>
  api.put<{ id: string }>(`/api/copiloto/accesos/${userId}`, { rol, activo: true });

export const quitarAcceso = (userId: string) =>
  api.del<{ ok: boolean }>(`/api/copiloto/accesos/${userId}`);

/** Un botón que ofrece el copiloto: una pantalla YA filtrada. */
export interface BotonCopiloto {
  ruta: string;
  filtros: Record<string, unknown>;
  etiqueta: string;
  /** "Muéstrame": id de la guía que va iluminando dónde tocar. */
  receta?: string;
}

export interface RespuestaCopiloto {
  texto: string;
  botones: BotonCopiloto[];
  /**
   * `true` mientras el asistente se está configurando a sí mismo.
   *
   * La ventanita lo avisa: quien está hablando tiene que saber que todavía no
   * puede preguntar por el negocio, y que lo que está haciendo es ponerlo en
   * marcha.
   */
  puestaEnMarcha?: boolean;
}

/** Un mensaje del hilo, en el formato que ya usa el modelo. */
export interface MensajeHilo {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Hablarle al copiloto desde la VENTANITA flotante.
 *
 * NO es de admin: quién puede hablarle lo decide el directorio, no el rol del
 * panel. Si la persona no está, el server contesta explicando por qué.
 *
 * Manda desde qué puerta se habló: el historial del dueño distingue la
 * ventanita de la pantalla del asistente.
 */
export const mandarMensaje = (texto: string) =>
  api.post<RespuestaCopiloto>('/api/copiloto/mensaje', { texto, origen: 'panel-flotante' });

/** El hilo de quien está mirando. Es el MISMO que el de WhatsApp. */
export const traerHilo = () =>
  api.get<{ mensajes: MensajeHilo[] }>('/api/copiloto/hilo');

/**
 * Empezar de nuevo: abre una conversación nueva. YA NO BORRA NADA.
 *
 * Un copiloto que arrastra un malentendido no se recupera solo, y el hilo nuevo
 * es lo que le hace olvidar. La conversación anterior queda guardada: el dueño
 * tiene que poder leerla después.
 */
export const empezarDeNuevo = () =>
  api.post<{ hilo: HiloCopiloto | null }>('/api/copiloto/hilos');

/** Un hilo de conversación. Solo uno está activo: es el que usa WhatsApp. */
export interface HiloCopiloto {
  id: string;
  titulo: string;
  activo: boolean;
  updatedAt?: string;
}

export interface ResultadoDeBusqueda {
  hiloId: string;
  titulo: string;
  fragmento: string;
  cuando: string;
}

/** Los hilos de quien está mirando, los más recientes primero. */
export const listarHilos = () => api.get<{ hilos: HiloCopiloto[] }>('/api/copiloto/hilos');

/** Abre uno nuevo y lo deja activo. El anterior queda en el historial. */
export const abrirHiloNuevo = () =>
  api.post<{ hilo: HiloCopiloto | null }>('/api/copiloto/hilos');

/** Lo que se habló en un hilo. Abrirlo NO lo vuelve el activo. */
export const traerHilo2 = (id: string) =>
  api.get<{ mensajes: MensajeHilo[] }>(`/api/copiloto/hilos/${id}`);

/** Busca dentro de lo que se habló, no solo en los títulos. */
export const buscarEnLosHilos = (q: string) =>
  api.get<{ resultados: ResultadoDeBusqueda[] }>(
    `/api/copiloto/buscar?q=${encodeURIComponent(q)}`,
  );

/** Lo que devuelve subir un adjunto: una foto para ver, o un audio ya en texto. */
export type Adjunto =
  | { tipo: 'imagen'; clave: string; url: string; dataBase64: string; mediaType: string }
  | { tipo: 'audio'; texto: string | null; error?: string };

/**
 * Sube una foto o una nota de voz.
 *
 * La foto vuelve en base64 para que el modelo la VEA; el audio vuelve ya
 * transcripto, listo para mandar como texto. El audio no se guarda.
 */
export async function subirAdjunto(archivo: File): Promise<Adjunto> {
  const form = new FormData();
  form.append('file', archivo);
  return api.post<Adjunto>('/api/copiloto/adjunto', form);
}

/** Mandarle un mensaje desde la pantalla del asistente, con una foto opcional. */
export const mandarMensajeConFoto = (
  texto: string,
  imagen?: { clave: string; dataBase64: string; mediaType: string },
) =>
  api.post<RespuestaCopiloto>('/api/copiloto/mensaje', {
    texto,
    imagen,
    origen: 'panel-asistente',
  });
